import React from "react";
import {
  IonContent,
  IonPage,
  useIonViewWillLeave,
  useIonViewWillEnter,
} from "@ionic/react";
import { useParams } from "react-router-dom";

import { IOnClickStudentAvatarParams } from "../../../state";
import {
  clearSuccessMessage,
  Context,
  CREATE_COMMENT_SUCCESS_MESSAGE,
  UPDATE_COMMENT_SUCCESS_MESSAGE,
  DELETE_COMMENT_SUCCESS_MESSAGE,
  CREATE_REALIZATION_STAMP_REACTION_SUCCESS,
  fetchComments,
  createComment,
  updateComment,
  deleteComment,
  clearComments,
  clearFeedbacks,
  fetchGearthemeRealization,
  clearGearthemePagination,
  clearRealizations,
  createRealizationStampReaction,
  fetchRealizationConversations,
  clearConversations,
} from "../../../store/teacher";
import useWaitApiCall from "../../../hooks/useWaitApiCall";
import ThemeHeader from "../../../molecules/ThemeHeader";
import EmbeddedHeader from "../../../organisms/teacher/Header/EmbeddedHeader";
import EmbeddedHeaderModal from "../../../organisms/EmbeddedHeaderModal";
import TimelineTheme from "../../../templates/teacher/TimelineTheme";
import SharedRealization from "../../../templates/teacher/SharedRealization";
import type { Stamp } from "../../../libs/stamp";
import styles from "../common.module.scss";

const TimelineThemePage = () => {
  const { dispatch, contextState } = React.useContext(Context);
  const [calling, peep] = useWaitApiCall(contextState);
  const params = useParams<{ id: string }>();
  const [values, updateValues] = React.useState({
    isDisplaying: false,
    realizationDetailId: 0,
  });
  const update = (args: { [key: string]: any }) => {
    updateValues(prevValues => ({ ...prevValues, ...args }));
  };

  const loadRealizations = React.useCallback(() => {
    dispatch(peep(clearSuccessMessage()));
    const list = contextState.realizations;
    dispatch(
      peep(
        fetchGearthemeRealization(Number(params.id), list[list.length - 1].id),
      ),
    );
  }, [dispatch, peep, contextState.realizations, params.id]);

  useIonViewWillEnter(() => {
    update({
      isDisplaying: true,
    });
    dispatch(peep(fetchGearthemeRealization(Number(params.id), null)));
  }, []);

  useIonViewWillLeave(() => {
    update({
      isDisplaying: false,
    });
    clearRealizationFunc();
    dispatch(clearGearthemePagination());
    dispatch(clearRealizations());
  });

  const realizationDetail = React.useMemo(
    () =>
      contextState.realizations.find(
        realization => realization.id === values.realizationDetailId,
      ),
    [contextState.realizations, values.realizationDetailId],
  );

  React.useEffect(() => {
    if (values.realizationDetailId !== 0) {
      dispatch(fetchComments(values.realizationDetailId));
      dispatch(fetchRealizationConversations(values.realizationDetailId));
    }
  }, [dispatch, values.realizationDetailId]);

  React.useEffect(() => {
    if (
      values.realizationDetailId !== 0 &&
      (contextState.success_message === CREATE_COMMENT_SUCCESS_MESSAGE ||
        contextState.success_message === UPDATE_COMMENT_SUCCESS_MESSAGE ||
        contextState.success_message === DELETE_COMMENT_SUCCESS_MESSAGE ||
        contextState.success_message ===
          CREATE_REALIZATION_STAMP_REACTION_SUCCESS)
    ) {
      dispatch(clearSuccessMessage());
      dispatch(fetchComments(values.realizationDetailId));
    }
  }, [dispatch, contextState.success_message, values.realizationDetailId]);

  const createCommentFunc = React.useCallback(
    (realization_id: number, content: string) => {
      dispatch(peep(createComment(realization_id, content)));
    },
    [dispatch, peep],
  );

  const updateCommentFunc = React.useCallback(
    (comment_id: number, content: string) => {
      dispatch(peep(updateComment(comment_id, content)));
    },
    [dispatch, peep],
  );

  const deleteCommentFunc = React.useCallback(
    (comment_id: number) => {
      dispatch(peep(deleteComment(comment_id)));
    },
    [dispatch, peep],
  );

  const createStampReactionFunc = (realization_id: number, stamp: Stamp) => {
    dispatch(
      peep(
        createRealizationStampReaction(realization_id, {
          type: "comment",
          realization_stamp_id: stamp.id,
        }),
      ),
    );
  };

  const clearRealizationFunc = React.useCallback(() => {
    update({ realizationDetailId: 0 });
    dispatch(clearComments());
    dispatch(clearFeedbacks());
    dispatch(clearConversations());
  }, [dispatch]);

  const showStudentDetailFunc = ({
    class_id,
    student_id,
  }: IOnClickStudentAvatarParams) => {
    window.location.href = `${process.env.REACT_APP_TEACHER_ONE_MENU_URL}/students/${class_id}/${student_id}`;
  };

  const teacherClassIds = React.useMemo(
    () => contextState.teacher.classes.map(c => c.id),
    [contextState.teacher.classes],
  );

  return (
    <IonPage>
      {realizationDetail && (
        <EmbeddedHeaderModal
          isOpen={!!values.realizationDetailId}
          title="詳細"
          onClose={clearRealizationFunc}
        >
          <SharedRealization
            calling={calling}
            realization={realizationDetail}
            comments={contextState.comments}
            conversations={contextState.conversations}
            current_id={contextState.teacher.id}
            createComment={createCommentFunc}
            updateComment={updateCommentFunc}
            deleteComment={deleteCommentFunc}
            createStampReaction={createStampReactionFunc}
            onAvatarClick={showStudentDetailFunc}
            teacherClassIds={teacherClassIds}
          />
        </EmbeddedHeaderModal>
      )}
      <IonContent className={`${styles.wrapper} ${styles.bgWhileBase}`}>
        {contextState.teacher.classes.length > 0 && (
          <div className={styles.container}>
            <EmbeddedHeader title="みんなの回答" />
            <div className={styles.hasOverflowY}>
              {contextState.geartheme && (
                <ThemeHeader geartheme={contextState.geartheme} isTeacher />
              )}
              <TimelineTheme
                isDisplaying={values.isDisplaying}
                calling={calling}
                realizations={contextState.realizations}
                geartheme_pagination={contextState.geartheme_pagination}
                fetchRealization={loadRealizations}
                onAction={realizationDetailId =>
                  update({ realizationDetailId })
                }
                showStudentDetail={showStudentDetailFunc}
              />
            </div>
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default TimelineThemePage;
